/**
 * Lado DASHBOARD del puente builder. Abre la app del cliente en un popup,
 * en modo builder, y expone una API para pedirle que seleccione un elemento
 * o previsualice una guía. Pensado para importarse en el dashboard
 * (`import { createBuilderSession } from '@veo/sdk/builder'`).
 *
 * Seguridad: solo procesa mensajes cuyo `origin` sea el de la app y cuyo
 * `token` coincida con el de esta sesión.
 */
import { hasWindow } from '../../utils/safe-env';
import type { PreviewGuideInput } from '../guides/guide-preview';
import {
  BUILDER_ORIGIN_PARAM,
  BUILDER_TOKEN_PARAM,
  type BuilderCommand,
  VEO_BUILDER_SOURCE,
} from './bridge-protocol';
import type { PickedElement } from './element-picker';

export interface BuilderSessionOptions {
  /** URL de la app del cliente a abrir en modo builder. */
  appUrl: string;
  /** Token de la sesión (idealmente emitido/firmado por el backend). */
  token: string;
  /** Origen al que la app debe responder. Default: `location.origin`. */
  dashboardOrigin?: string;
  /** La app está lista y escuchando comandos. */
  onReady?: () => void;
  /** El usuario eligió un elemento. */
  onPicked?: (picked: PickedElement) => void;
  /** El usuario canceló la selección (Esc). */
  onCancelled?: () => void;
  /** La app cerró el modo builder (o el popup se cerró). */
  onClosed?: () => void;
  /**
   * Si se pasa, la app se embebe en este `<iframe>` (modo estudio in-app) en
   * lugar de abrirse en un popup. El lado app ya habla con `window.parent`,
   * así que el puente funciona igual. La sesión setea el `src` del iframe.
   */
  frame?: HTMLIFrameElement;
  /** Nombre de la ventana popup. Default: `veo-builder`. */
  windowName?: string;
  /** Features del popup. Default: ventana de ~1280×860. */
  windowFeatures?: string;
}

export interface BuilderSession {
  /** Pide a la app que entre en modo selección de elemento. */
  startPicker(options?: { maxAncestors?: number; hint?: string }): void;
  /** Cancela la selección en curso. */
  stopPicker(): void;
  /** Pide a la app que previsualice una guía. */
  preview(guide: PreviewGuideInput): void;
  /** Cierra la preview activa en la app. */
  closePreview(): void;
  /** Cierra el popup y desconecta el puente. */
  close(): void;
}

const DEFAULT_FEATURES = 'popup,width=1280,height=860';
const POPUP_POLL_MS = 600;

export function createBuilderSession(options: BuilderSessionOptions): BuilderSession {
  if (!hasWindow()) return noopSession();

  const dashboardOrigin = options.dashboardOrigin ?? window.location.origin;
  const appOrigin = safeOrigin(options.appUrl);
  const url = buildAppUrl(options.appUrl, options.token, dashboardOrigin);

  const frame = options.frame ?? null;
  const useFrame = frame !== null;

  let popup: Window | null = null;
  if (useFrame) {
    // biome-ignore lint/style/noNonNullAssertion: useFrame garantiza frame
    frame!.src = url;
  } else {
    popup = window.open(
      url,
      options.windowName ?? 'veo-builder',
      options.windowFeatures ?? DEFAULT_FEATURES,
    );
  }

  /** Ventana destino de los comandos: el popup o el contentWindow del iframe. */
  const targetWindow = (): Window | null => (useFrame ? (frame?.contentWindow ?? null) : popup);

  const post = (command: BuilderCommand): void => {
    const w = targetWindow();
    if (!w) return;
    if (!useFrame && popup?.closed) return;
    w.postMessage(
      { source: VEO_BUILDER_SOURCE, token: options.token, ...command },
      appOrigin ?? '*',
    );
  };

  let pollTimer: ReturnType<typeof setInterval> | undefined;

  const cleanup = (): void => {
    window.removeEventListener('message', onMessage);
    if (pollTimer !== undefined) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };

  const onMessage = (event: MessageEvent): void => {
    if (appOrigin && event.origin !== appOrigin) return;
    const data = event.data as {
      source?: unknown;
      token?: unknown;
      type?: unknown;
      payload?: unknown;
    } | null;
    if (!data || data.source !== VEO_BUILDER_SOURCE || data.token !== options.token) return;

    switch (data.type) {
      case 'ready':
        options.onReady?.();
        break;
      case 'picked':
        if (data.payload) options.onPicked?.(data.payload as PickedElement);
        break;
      case 'pick-cancelled':
        options.onCancelled?.();
        break;
      case 'closed':
        cleanup();
        options.onClosed?.();
        break;
    }
  };

  window.addEventListener('message', onMessage);

  // Detectar cierre manual del popup (el usuario cierra la ventana). En modo
  // iframe el ciclo de vida lo controla el dashboard (no hay nada que pollear).
  if (!useFrame) {
    pollTimer = setInterval(() => {
      if (popup?.closed) {
        cleanup();
        options.onClosed?.();
      }
    }, POPUP_POLL_MS);
  }

  return {
    startPicker: (o) =>
      post({
        type: 'start-picker',
        ...(o?.maxAncestors !== undefined ? { maxAncestors: o.maxAncestors } : {}),
        ...(o?.hint !== undefined ? { hint: o.hint } : {}),
      }),
    stopPicker: () => post({ type: 'stop-picker' }),
    preview: (guide) => post({ type: 'preview', guide }),
    closePreview: () => post({ type: 'close-preview' }),
    close: () => {
      post({ type: 'teardown' });
      if (useFrame) {
        if (frame) frame.src = 'about:blank';
      } else if (popup && !popup.closed) {
        popup.close();
      }
      cleanup();
    },
  };
}

function buildAppUrl(appUrl: string, token: string, dashboardOrigin: string): string {
  try {
    const u = new URL(appUrl, window.location.href);
    u.searchParams.set(BUILDER_TOKEN_PARAM, token);
    u.searchParams.set(BUILDER_ORIGIN_PARAM, dashboardOrigin);
    return u.toString();
  } catch {
    return appUrl;
  }
}

function safeOrigin(url: string): string | null {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
}

function noopSession(): BuilderSession {
  return {
    startPicker: () => {},
    stopPicker: () => {},
    preview: () => {},
    closePreview: () => {},
    close: () => {},
  };
}
